import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  Inject,
  AccumulationDataLabel,
  AccumulationTooltip,
} from "@syncfusion/ej2-react-charts";
import {
  FaUsers,
  FaBriefcase,
  FaChalkboardTeacher,
  FaBookOpen,
  FaBook,
  FaBullhorn,
  FaCalendarAlt,
} from "react-icons/fa";
import { Header } from "../Components";
import axios from "axios";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

const Dash = () => {
  const [dashboard, setDashboard] = useState({
    contacts: 0,
    inquiries: 0,
    placements: 0,
    faculty: 0,
    syllabus: 0,
    courses: 0,
    circulars: 0,
    events: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { baseUrl } = useBaseUrl();

  // Chart data initialized with default values
  const [chartData, setChartData] = useState([
    { x: "Jan 2025", y: 0, text: "0" },
    { x: "Feb 2025", y: 0, text: "0" },
    { x: "Mar 2025", y: 0, text: "0" },
    { x: "Apr 2025", y: 0, text: "0" },
    { x: "May 2025", y: 0, text: "0" },
    { x: "Jun 2025", y: 0, text: "0" },
  ]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboard = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const formData = new FormData();
        formData.append("action", "dashboard");
        const response = await axios.post(
          `${baseUrl}/dashboard.php`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          }
        );

        if (response.data.status) {
          const apiData = response.data.data;
          // Map API response to dashboard state
          setDashboard({
            contacts: apiData.contact_count || 0,
            inquiries: apiData.admission_inquiry_count || 0,
            placements: apiData.placement_count || 0,
            faculty: apiData.faculty_count || 0,
            syllabus: apiData.syllabus_count || 0,
            courses: apiData.course_count || 0,
            circulars: apiData.circular || 0,
            events: apiData.event_news || 0,
          });

          // Update chart data with monthly inquiry if available
          if (apiData.monthly_inquiry) {
            setChartData((prevChartData) =>
              prevChartData.map((item) => {
                const month = item.x.split(" ")[0]; // Extract month from "Month Year"
                const count = apiData.monthly_inquiry[month] || 0;
                return { ...item, y: count, text: count.toString() };
              })
            );
          }
        } else {
          setErrorMessage(
            response.data.error || "Failed to fetch dashboard data."
          );
        }
      } catch (error) {
        setErrorMessage(
          "Error connecting to the server: " +
            (error.response?.data?.error || error.message)
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [baseUrl]);

  const { setCurrentColor, setCurrentMode, currentMode } = useStateContext();

  useEffect(() => {
    const currentThemeColor = localStorage.getItem("colorMode");
    const currentThemeMode = localStorage.getItem("themeMode");
    if (currentThemeColor && currentThemeMode) {
      setCurrentColor(currentThemeColor);
      setCurrentMode(currentThemeMode);
    }
  }, []);

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fadeIn bg-gray-100 min-h-screen"
    >
      <Header category="Dashboard" title="Overview" />

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          {errorMessage}
        </div>
      )}

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Link
          to="/contact"
          className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300"
        >
          <div className="bg-blue-500 text-white p-4 rounded-full">
            <FaUsers size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Contact-List
            </h3>
            <p className="text-2xl font-bold text-gray-700">
              {dashboard.contacts}
            </p>
          </div>
        </Link>
        <Link
          to="/inquiry"
          className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300"
        >
          <div className="bg-blue-500 text-white p-4 rounded-full">
            <FaUsers size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Inquiries</h3>
            <p className="text-2xl font-bold text-gray-700">
              {dashboard.inquiries}
            </p>
          </div>
        </Link>
        <Link
          to="/placement"
          className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300"
        >
          <div className="bg-green-500 text-white p-4 rounded-full">
            <FaBriefcase size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Placements</h3>
            <p className="text-2xl font-bold text-gray-700">
              {dashboard.placements}
            </p>
          </div>
        </Link>
        <Link
          to="/faculty"
          className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300"
        >
          <div className="bg-purple-500 text-white p-4 rounded-full">
            <FaChalkboardTeacher size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Faculty</h3>
            <p className="text-2xl font-bold text-gray-700">
              {dashboard.faculty}
            </p>
          </div>
        </Link>
        <Link
          to="/syllabus"
          className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300"
        >
          <div className="bg-red-500 text-white p-4 rounded-full">
            <FaBookOpen size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Syllabus</h3>
            <p className="text-2xl font-bold text-gray-700">
              {dashboard.syllabus}
            </p>
          </div>
        </Link>
        <Link
          to="/course"
          className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300"
        >
          <div className="bg-yellow-500 text-white p-4 rounded-full">
            <FaBook size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Courses</h3>
            <p className="text-2xl font-bold text-gray-700">
              {dashboard.courses}
            </p>
          </div>
        </Link>
        <Link
          to="/circulars"
          className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300"
        >
          <div className="bg-orange-500 text-white p-4 rounded-full">
            <FaBullhorn size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Circulars</h3>
            <p className="text-2xl font-bold text-gray-700">
              {dashboard.circulars}
            </p>
          </div>
        </Link>
        <Link
          to="/events"
          className="bg-white rounded-xl shadow-lg p-6 flex items-center gap-4 hover:shadow-xl transition-shadow duration-300"
        >
          <div className="bg-pink-500 text-white p-4 rounded-full">
            <FaCalendarAlt size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Events</h3>
            <p className="text-2xl font-bold text-gray-700">
              {dashboard.events}
            </p>
          </div>
        </Link>
      </div>

      {/* Chart Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Inquiry Trends (2025)
        </h2>
        <AccumulationChartComponent
          id="inquiry-chart"
          tooltip={{ enable: true }}
          legendSettings={{ visible: true }}
        >
          <Inject services={[AccumulationDataLabel, AccumulationTooltip]} />
          <AccumulationSeriesCollectionDirective>
            <AccumulationSeriesDirective
              dataSource={chartData}
              xName="x"
              yName="y"
              dataLabel={{ visible: true, name: "text", position: "Outside" }}
              type="Pie"
            />
          </AccumulationSeriesCollectionDirective>
        </AccumulationChartComponent>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Links</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/contact-list"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 text-center"
          >
            Contact List
          </Link>
          <Link
            to="/placement"
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-300 text-center"
          >
            Placement
          </Link>
          <Link
            to="/faculty"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition duration-300 text-center"
          >
            Faculty
          </Link>
          <Link
            to="/syllabus"
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 text-center"
          >
            Syllabus
          </Link>
          <Link
            to="/courses"
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition duration-300 text-center"
          >
            Courses
          </Link>
          <Link
            to="/circulars"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition duration-300 text-center"
          >
            Circulars
          </Link>
          <Link
            to="/events"
            className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition duration-300 text-center"
          >
            Events
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dash;
